import type {
  ClinicalBillingPath,
  ClinicalBookingStep,
  ClinicalDraftSubPhase,
  ClinicalProductStep,
  ClinicalReservedSubPhase,
} from '@/stores/clinicalConsultationBookingFlow'

export type ClinicalBookingChromeKey =
  | 'intro'
  | 'existing_consultation_gate'
  | 'appointment_cancelled'
  | 'reservation_expired'
  | 'unsupported_state'
  | 'profile'
  | 'insurance_category'
  | 'cash_pay_eligible'
  | 'insurance_on_file'
  | 'insurance_details'
  | 'insurance_verified'
  | 'slot_picker'
  | 'hard_verification'
  | 'ensuring_bridge_service'
  | 'payment_pending'
  | 'pending_settlement_payment'
  | 'provisioning'
  | 'scheduled'
  | 'loading'

export type ClinicalBookingChrome = {
  title?: string
  subtitle?: string
}

export type ClinicalBookingLayoutMode = 'fullpage' | 'popup'

const FULLPAGE_LAYOUT_CHROME_KEYS = new Set<ClinicalBookingChromeKey>([
  'intro',
  'profile',
  'insurance_category',
  'insurance_details',
  'slot_picker',
  'payment_pending',
  'pending_settlement_payment',
])

export function resolveClinicalBookingLayoutMode(
  chromeKey: ClinicalBookingChromeKey,
): ClinicalBookingLayoutMode {
  return FULLPAGE_LAYOUT_CHROME_KEYS.has(chromeKey) ? 'fullpage' : 'popup'
}

export function resolveClinicalBookingChromeKey(args: {
  isResumingConsultation: boolean
  isAvailabilityLoading: boolean
  showProductPreflow: boolean
  activeProductStep: ClinicalProductStep | null
  currentStep: ClinicalBookingStep
  draftSubPhase: ClinicalDraftSubPhase
  reservedSubPhase: ClinicalReservedSubPhase
  billingPath?: ClinicalBillingPath | null
  isContinuingBooking?: boolean
  hasScheduledConsultation?: boolean
  inPostPaymentConfirmationSession?: boolean
}): ClinicalBookingChromeKey {
  if (args.isResumingConsultation) {
    return 'loading'
  }

  if (args.showProductPreflow) {
    if (args.activeProductStep === 'intro') return 'intro'
    if (args.activeProductStep === 'existing_consultation_gate') return 'existing_consultation_gate'
    if (args.activeProductStep === 'unsupported_state') return 'unsupported_state'
    if (args.activeProductStep === 'profile') return 'profile'
    if (args.activeProductStep === 'insurance_category') return 'insurance_category'
    if (args.activeProductStep === 'cash_pay_eligible') return 'cash_pay_eligible'
    if (args.activeProductStep === 'insurance_on_file') return 'insurance_on_file'
    if (args.activeProductStep === 'insurance_details') return 'insurance_details'
    if (args.activeProductStep === 'insurance_verified') return 'insurance_verified'
    if (args.activeProductStep === 'appointment_cancelled') return 'appointment_cancelled'
  }

  if (
    args.isAvailabilityLoading &&
    args.currentStep === 'draft' &&
    args.draftSubPhase === 'slot-picker'
  ) {
    return 'loading'
  }

  if (args.currentStep === 'reservation_expired') return 'reservation_expired'

  if (args.currentStep === 'draft' && args.draftSubPhase === 'slot-picker') return 'slot_picker'
  if (args.currentStep === 'reserved' && args.reservedSubPhase === 'insured_coverage') {
    return 'hard_verification'
  }
  if (args.currentStep === 'ensuring_bridge_service' && args.billingPath === 'insured') {
    return 'hard_verification'
  }
  if (args.currentStep === 'ensuring_bridge_service') return 'ensuring_bridge_service'
  if (args.currentStep === 'payment_pending') return 'payment_pending'
  if (args.currentStep === 'pending_settlement_payment') return 'pending_settlement_payment'
  if (args.currentStep === 'provisioning') return 'provisioning'
  if (args.currentStep === 'scheduled') {
    if (args.inPostPaymentConfirmationSession) {
      return 'scheduled'
    }
    if (args.hasScheduledConsultation) {
      return 'existing_consultation_gate'
    }
    return 'scheduled'
  }

  return 'loading'
}

export type ClinicalBookingChromeTable = Record<ClinicalBookingChromeKey, ClinicalBookingChrome>

export const CLINICAL_PAYMENT_PENDING_CHROME_SUBTITLE_INSURED =
  'Review your visit details, insurance, and cost below. Complete payment to schedule your consultation.'

export const CLINICAL_PAYMENT_PENDING_CHROME_SUBTITLE_INSURED_ZERO_PR =
  'Review your visit details and insurance below. Save a card on file to schedule your consultation. Nothing is due today.'

export const CLINICAL_PAYMENT_PENDING_CHROME_SUBTITLE_SELF_PAY =
  'Review your visit details and cost below. Complete payment to schedule your consultation.'

export function resolveClinicalBookingChromeContextKey(args: {
  entrySource?: 'account' | 'signup'
  isContinuingBooking?: boolean
  unsupportedStateName?: string
  isInsuredPath?: boolean
  insuredPatientResponsibilityCents?: number | null
  scheduledByCareTeam?: boolean
}): ClinicalBookingChromeTable {
  // const unsupportedState = args.unsupportedStateName?.trim() || 'your state'

  const CLINICAL_BOOKING_CHROME: Record<ClinicalBookingChromeKey, ClinicalBookingChrome> = {
    intro: {
      title: '',
      subtitle: undefined,
    },
    existing_consultation_gate: {
      title: '',
      subtitle: undefined,
    },
    appointment_cancelled: {
      title: undefined,
      subtitle: undefined,
    },
    reservation_expired: {
      title: 'Your slot reservation expired',
      subtitle: 'Please start booking again.',
    },
    unsupported_state: {
      title: undefined, //`We're sorry — consultations aren't available in ${unsupportedState} yet`,
      subtitle: undefined, //`We don't yet have a licensed provider in ${unsupportedState}, but we're rapidly expanding our clinical team to new states and will let you know as soon as consultations open up where you live.`,
    },
    profile: {
      title: 'Confirm your information is accurate',
      subtitle: "We'll use these details to check your insurance coverage.",
    },
    insurance_category: {
      title: 'What kind of insurance do you have?',
      subtitle: 'Select the option that best describes your health insurance.',
    },
    cash_pay_eligible: {
      title: '',
      subtitle: undefined,
    },
    insurance_on_file: {
      title: '',
      subtitle: undefined,
    },
    insurance_details: {
      title: 'Insurance information',
      subtitle:
        'Before booking, please share your insurance information so we can match you with an in-network provider.',
    },
    insurance_verified: {
      title: '',
      subtitle: undefined,
    },
    slot_picker: {
      title: 'Select date & time',
      subtitle: 'Choose an available appointment slot for your consultation.',
    },
    hard_verification: {
      title: '',
      subtitle: undefined,
    },
    ensuring_bridge_service: {
      title: 'Preparing your visit',
      subtitle:
        'We are setting up billing with your insurance provider. This usually takes a moment.',
    },
    payment_pending: {
      title: args.scheduledByCareTeam
        ? 'Complete payment to confirm your visit'
        : args.isInsuredPath && args.insuredPatientResponsibilityCents === 0
          ? 'Save a card to secure your booking'
          : 'Complete payment to secure your booking',
      subtitle: args.scheduledByCareTeam
        ? 'Review your visit details, insurance, and cost below.'
        : args.isInsuredPath && args.insuredPatientResponsibilityCents === 0
          ? CLINICAL_PAYMENT_PENDING_CHROME_SUBTITLE_INSURED_ZERO_PR
          : args.isInsuredPath
            ? CLINICAL_PAYMENT_PENDING_CHROME_SUBTITLE_INSURED
            : CLINICAL_PAYMENT_PENDING_CHROME_SUBTITLE_SELF_PAY,
    },
    pending_settlement_payment: {
      title: 'Complete your settlement payment',
      subtitle:
        'Your visit is complete. Pay the remaining balance below to close out your consultation.',
    },
    provisioning: {
      title: 'Payment received',
      subtitle: 'Your clinical consultation is being scheduled.',
    },
    scheduled: {
      title: '',
      subtitle: undefined,
    },
    loading: {
      title: 'Loading',
      subtitle: 'Please wait…',
    },
  }

  return CLINICAL_BOOKING_CHROME
}

export function resolveClinicalBookingChrome(args: {
  chromeKey: ClinicalBookingChromeKey
  entrySource?: 'account' | 'signup'
  isContinuingBooking?: boolean
  unsupportedStateName?: string
  isInsuredPath?: boolean
  insuredPatientResponsibilityCents?: number | null
  scheduledByCareTeam?: boolean
}): ClinicalBookingChrome {
  return resolveClinicalBookingChromeContextKey(args)[args.chromeKey]
}
